/**
 * Rewrite a Supabase Storage public object URL to the on-the-fly image
 * transformer endpoint so we serve a resized, re-compressed variant
 * instead of the original multi-megabyte file. Supabase auto-negotiates
 * WebP via the Accept header when the source isn't already WebP.
 *
 * Non-Supabase URLs (Unsplash, external CDN, CDN-hosted assets, blob:/data:)
 * are returned untouched.
 */
export function optimizeImg(
  url: string | null | undefined,
  width: number,
  opts: {
    quality?: number;
    resize?: "cover" | "contain" | "fill";
    height?: number;
    /**
     * Display aspect ratio (width / height). When set (and no explicit
     * height), the transform crops to that ratio instead of preserving the
     * source ratio. Critical for panorama/portrait uploads: a 200px-wide
     * request on a 1:8 source otherwise renders 200x1563 — tens of KB the
     * card never shows because CSS `object-cover` crops it anyway.
     */
    aspect?: number;
    /**
     * Internal: skip the DPR multiplier. Used by `optimizeSrcSet` so the
     * URL width matches the `w` descriptor, letting the browser (not us)
     * pick the correct DPR variant. Also skip DPR when passing a raw
     * pixel width you want to fetch exactly.
     */
    raw?: boolean;
  } = {},
): string {
  if (!url) return "";
  if (url.startsWith("data:") || url.startsWith("blob:")) return url;
  const marker = "/storage/v1/object/public/";
  const renderMarker = "/storage/v1/render/image/public/";
  const objectIdx = url.indexOf(marker);
  const renderIdx = url.indexOf(renderMarker);
  if (objectIdx === -1 && renderIdx === -1) return url; // not a Supabase Storage object

  const q = Math.max(20, Math.min(90, Math.round(opts.quality ?? 65)));
  // No DPR multiplier: it would differ between SSR (no `window`) and the
  // client, producing a different `src`/`srcSet` URL after hydration and
  // triggering a React hydration mismatch on every optimized image.
  // The browser already picks the right candidate from `srcSet` width
  // descriptors based on its own DPR, so this is the correct behavior.
  const dpr = 1;
  void opts.raw;
  const w = Math.max(32, Math.min(1920, Math.round(width * dpr)));

  const resize = opts.resize ?? "cover";
  const withoutQuery = url.split("?")[0];
  // Bucket-relative path (e.g. "content-images/places/helsinki/xyz.webp").
  const objectPath = renderIdx === -1
    ? withoutQuery.slice(objectIdx + marker.length)
    : withoutQuery.slice(renderIdx + renderMarker.length);

  // Serve through the same-origin proxy (src/routes/i.$.ts): it re-emits the
  // Supabase-transformed bytes with a one-year immutable cache instead of
  // Supabase's 1-hour max-age, and keeps images on the document's connection.
  let out = `/i/${objectPath}?width=${w}&quality=${q}&resize=${resize}`;
  const rawHeight = opts.height ?? (opts.aspect ? width / opts.aspect : undefined);
  if (rawHeight) {
    const h = Math.max(32, Math.min(1920, Math.round(rawHeight * dpr)));
    out += `&height=${h}`;
  }
  return out;
}

/** Absolute Supabase URL — for og:image / share previews, where a
 *  relative proxy path is not usable by crawlers. */
export function absoluteImg(url: string | null | undefined, width: number, quality = 70): string {
  if (!url) return "";
  const proxied = optimizeImg(url, width, { quality });
  if (!proxied.startsWith("/i/")) return proxied;
  return `https://whatfinland.com${proxied}`;
}


/** Build a srcset string for responsive images at multiple widths. */
export function optimizeSrcSet(
  url: string | null | undefined,
  widths: number[],
  opts?: { quality?: number; resize?: "cover" | "contain" | "fill"; aspect?: number },
): string {
  if (!url) return "";
  if (
    !url.includes("/storage/v1/object/public/") &&
    !url.includes("/storage/v1/render/image/public/")
  ) return "";

  // Pass raw:true so URL width == descriptor width — the browser then
  // multiplies by its own DPR when picking the candidate.
  return widths
    .map((w) => `${optimizeImg(url, w, { ...opts, raw: true })} ${w}w`)
    .join(", ");
}
